// Generic localStorage-backed draft hook (Spår 3 delspår 3a Q34).
//
// Auto-persists value to localStorage on change. Reads on mount and falls back
// to defaultValue if key missing or parse-error. SSR-safe via typeof window guard.
// Cross-tab coordination: value-payload wraps { value, savedAt: timestamp }
// so concurrent tabs can detect later-write-wins on read.
//
// Per Fas 0 sektion D — first custom localStorage hook in codebase. Establishes
// `boply:` namespace convention going forward (kodbasen saknade prefix-konvention
// helt — bonus-fynd flagged 2026-05-27).

import { useCallback, useEffect, useState } from "react";

interface DraftPayload<T> {
  value: T;
  savedAt: number; // ms-epoch when persisted
}

function isDraftPayload<T>(raw: unknown): raw is DraftPayload<T> {
  return (
    typeof raw === "object" &&
    raw !== null &&
    "value" in raw &&
    "savedAt" in raw &&
    typeof (raw as { savedAt: unknown }).savedAt === "number"
  );
}

function readKey<T>(key: string): { value: T; savedAt: number } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isDraftPayload<T>(parsed)) return null;
    return parsed;
  } catch {
    // Corrupt JSON or restricted storage — fall through to defaultValue.
    return null;
  }
}

function removeKey(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Restricted storage — nothing to clean up.
  }
}

function writeKey<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  try {
    const payload: DraftPayload<T> = { value, savedAt: Date.now() };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // Quota exceeded or restricted — silently skip persistence.
  }
}

export interface UseLocalStorageDraftReturn<T> {
  value: T;
  setValue: (next: T) => void;
  /**
   * Reset value to defaultValue + remove localStorage entry.
   * Use on wizard-complete or explicit-abandon.
   */
  clearDraft: () => void;
  /**
   * Last-persisted-timestamp of value, or null if never persisted.
   * Used for cross-tab race detection (Q34 mitigation per Fas 0 sektion J).
   */
  savedAt: number | null;
  /**
   * True if there was a non-empty value in localStorage at mount time.
   * Used to show "Continue draft?"-dialog at wizard-entry.
   */
  hasInitialDraft: boolean;
}

export function useLocalStorageDraft<T>(
  key: string,
  defaultValue: T,
  /**
   * Drop a stored draft older than this (ms) at mount instead of surfacing it.
   * Stale-draft hygiene: a forgotten draft must never be the only thing standing
   * between a user and the wizard — it opens the recovery-dialog, and a blocked or
   * occluded dialog then strands the whole flow. Omit to keep drafts forever.
   */
  maxAgeMs?: number,
): UseLocalStorageDraftReturn<T> {
  // Lazy-init from localStorage. Per Fas 0 + Lesson 28 — useState init runs
  // once at mount; avoids stale-state on re-mount.
  const [state, setState] = useState<{ value: T; savedAt: number | null; hadInitial: boolean }>(
    () => {
      const stored = readKey<T>(key);
      if (
        stored &&
        maxAgeMs != null &&
        stored.savedAt != null &&
        Date.now() - stored.savedAt > maxAgeMs
      ) {
        removeKey(key);
        return { value: defaultValue, savedAt: null, hadInitial: false };
      }
      if (stored) return { value: stored.value, savedAt: stored.savedAt, hadInitial: true };
      return { value: defaultValue, savedAt: null, hadInitial: false };
    },
  );

  // Persist on value-change (idempotent — same value writes same payload + new
  // timestamp). Cross-tab via timestamp: when other tab writes, this tab's
  // value is stale but local-state reflects the read-at-mount-time.
  useEffect(() => {
    // Persist only after an intentional setValue (which stamps savedAt). A null
    // savedAt means the value is still the initial default (or was cleared) — never
    // write it. The previous guard also reference-compared `state.value === defaultValue`,
    // but callers recreate the defaultValue object every render, so that compare failed
    // on the first re-render and persisted the DEFAULT draft — which then spuriously
    // triggered the "resume draft?" recovery-dialog on the next visit (DEFECT D4, 3b runtime).
    if (state.savedAt === null) return;
    writeKey(key, state.value);
  }, [key, state.value, state.savedAt]);

  const setValue = useCallback((next: T) => {
    setState((prev) => ({ value: next, savedAt: Date.now(), hadInitial: prev.hadInitial }));
  }, []);

  const clearDraft = useCallback(() => {
    removeKey(key);
    setState({ value: defaultValue, savedAt: null, hadInitial: false });
  }, [key, defaultValue]);

  return {
    value: state.value,
    setValue,
    clearDraft,
    savedAt: state.savedAt,
    hasInitialDraft: state.hadInitial,
  };
}
